/**
 * Caja — mirror del desktop (`apps/desktop/src/renderer/src/pages/Cash.tsx`).
 *
 * Flujo:
 *   - Sin sesión: formulario de apertura.
 *   - Con sesión: tabs Resumen / Ventas / Movimientos / Cerrar turno.
 *
 * Diferencias vs desktop (cierran en iteraciones futuras):
 *   - Sin `SaleDetailModal` (modal con tickets/line items del pedido)
 *     porque el router de tickets aún no se portó al worker.
 *   - Sin permission gating — todos los roles que llegan ven todas las tabs.
 */

import { Button, Card, CardContent, CardHeader, CardTitle } from '@atreus/ui';
import { Link } from '@tanstack/react-router';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Bike,
  Coins,
  ConciergeBell,
  Download,
  Lock,
  Printer,
  Receipt,
  Store,
  Unlock,
  Users,
} from 'lucide-react';
import { type ComponentType, useState } from 'react';

import { loadSession } from '../auth/session.js';
import { StatCardsSkeleton } from '../components/skeletons.js';
import {
  SaleDetailModal,
  type SaleDetailSeed,
} from '../features/cash/SaleDetailModal.js';
import {
  buildCashReportCsv,
  cashReportFilename,
  downloadCsv,
} from '../lib/cashReport.js';
import { useFeatures } from '../lib/features.js';
import { formatCOP, parseCOPToCents } from '../lib/format.js';
import { orderStatusChipClass, orderStatusLabels } from '../lib/orders.js';
import {
  COLLECTION_METHODS,
  PAYMENT_METHODS,
  paymentMethodLabels,
} from '../lib/payments.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

import type { CollectionMethod, OrderOrigin, OrderStatus } from '@atreus/core';

const movementTypeLabels: Record<string, string> = {
  open: 'Apertura',
  close: 'Cierre',
  sale: 'Venta',
  refund: 'Reembolso',
  in: 'Ingreso',
  out: 'Egreso',
  drop: 'Retiro',
};

const originIcon = (origin: OrderOrigin) => {
  switch (origin) {
    case 'table':
      return ConciergeBell;
    case 'delivery':
      return Bike;
    case 'counter':
      return Store;
  }
};

const originLabels: Record<OrderOrigin, string> = {
  table: 'Mesa',
  delivery: 'Domicilio',
  counter: 'Mostrador',
};

type CashTab = 'summary' | 'sales' | 'movements' | 'close';

const TABS: readonly { value: CashTab; label: string }[] = [
  { value: 'summary', label: 'Resumen' },
  { value: 'sales', label: 'Ventas' },
  { value: 'movements', label: 'Movimientos' },
  { value: 'close', label: 'Cerrar turno' },
];

export const CashPage = () => {
  const utils = trpc.useUtils();
  const session = trpc.cash.currentSession.useQuery();
  const summary = trpc.cash.summary.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const movements = trpc.cash.listMovements.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const sales = trpc.cash.sales.useQuery(undefined, {
    refetchInterval: 5_000,
  });
  const otherSessions = trpc.cash.listOpenSessions.useQuery(undefined, {
    refetchInterval: 15_000,
  });
  const [tab, setTab] = useState<CashTab>('summary');
  const posSession = loadSession();
  const permissions = usePermissions();
  const canOpenOwn = permissions.canDo('cash.openOwn');
  const canCloseOwn = permissions.canDo('cash.closeOwn');
  const canAddMovement = permissions.canDo('cash.addMovement');

  const invalidateAll = () => {
    void utils.cash.currentSession.invalidate();
    void utils.cash.summary.invalidate();
    void utils.cash.listMovements.invalidate();
    void utils.cash.sales.invalidate();
    void utils.cash.history.invalidate();
  };

  const open = trpc.cash.openSession.useMutation({ onSuccess: invalidateAll });
  const addMov = trpc.cash.addMovement.useMutation({ onSuccess: invalidateAll });
  const close = trpc.cash.closeSession.useMutation({ onSuccess: invalidateAll });

  if (session.isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-8">
        <StatCardsSkeleton count={4} />
      </div>
    );
  }

  if (!session.data) {
    if (!canOpenOwn) {
      return (
        <div className="mx-auto max-w-md p-8 text-center">
          <h1 className="text-2xl font-black tracking-tight">Caja</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Tu rol no puede abrir caja. Contacta al administrador si
            necesitas este acceso.
          </p>
        </div>
      );
    }
    return (
      <OpenSessionForm
        onSubmit={(amountCents, note) =>
          open.mutate({
            openingCashCents: amountCents,
            ...(note !== undefined ? { note } : {}),
          })
        }
        error={open.error?.message ?? null}
        pending={open.isPending}
      />
    );
  }

  const salesCount = sales.data?.length ?? 0;
  const movementsCount = movements.data?.length ?? 0;
  const tabCounts: Partial<Record<CashTab, number>> = {
    sales: salesCount,
    movements: movementsCount,
  };

  const sessionId = session.data.id;

  const handleDownloadCsv = async (): Promise<void> => {
    // Pedimos el detalle on-demand para tener los pedidos y movimientos
    // frescos (no alcanza con `summary`, que solo tiene agregados).
    const d = await utils.cash.sessionDetail.fetch({ sessionId });
    const csv = buildCashReportCsv(d);
    downloadCsv(cashReportFilename(d), csv);
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Tu caja</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Abierta{' '}
            {posSession?.user.fullName
              ? `por ${posSession.user.fullName} · `
              : ''}
            {new Date(session.data.openedAt).toLocaleString('es-CO')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleDownloadCsv()}
            title="Descargar detalle del turno en CSV"
          >
            <Download className="size-4" /> CSV
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link
              to="/reports/cash/$sessionId"
              params={{ sessionId }}
              target="_blank"
              title="Abrir vista imprimible — Ctrl+P para guardar como PDF"
            >
              <Printer className="size-4" /> Imprimir
            </Link>
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {TABS.filter((t) => t.value !== 'close' || canCloseOwn).map((t) => {
          const active = tab === t.value;
          const count = tabCounts[t.value];
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={
                'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ' +
                (active
                  ? 'border-fg bg-fg text-fg-inverse'
                  : 'border-border bg-surface text-fg-muted hover:text-fg')
              }
            >
              <span>{t.label}</span>
              {count != null && count > 0 ? (
                <span
                  className={
                    'rounded-full px-1.5 text-[10px] font-bold ' +
                    (active
                      ? 'bg-fg-inverse/20 text-fg-inverse'
                      : 'bg-surface-muted text-fg-muted')
                  }
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {tab === 'summary' ? (
        <>
          {summary.data ? <SummaryBlock data={summary.data} /> : null}
          {otherSessions.data && otherSessions.data.length > 0 ? (
            <OtherOpenSessionsCard sessions={otherSessions.data} />
          ) : null}
        </>
      ) : null}

      {tab === 'sales' ? (
        <>
          <SalesCard sales={sales.data ?? []} />
          <CollectionsCard />
        </>
      ) : null}

      {tab === 'movements' ? (
        <>
          {canAddMovement ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <MovementForm
                kind="in"
                title="Ingreso de efectivo"
                icon={ArrowDownToLine}
                onSubmit={(amount, reason) =>
                  addMov.mutate({
                    type: 'in',
                    amountCents: amount,
                    ...(reason !== undefined ? { reason } : {}),
                  })
                }
                pending={addMov.isPending}
              />
              <MovementForm
                kind="out"
                title="Egreso / gasto"
                icon={ArrowUpFromLine}
                onSubmit={(amount, reason) =>
                  addMov.mutate({
                    type: 'out',
                    amountCents: amount,
                    ...(reason !== undefined ? { reason } : {}),
                  })
                }
                pending={addMov.isPending}
              />
            </div>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Historial de movimientos</CardTitle>
            </CardHeader>
            <CardContent>
              {movements.data && movements.data.length > 0 ? (
                <ul className="divide-y divide-border">
                  {movements.data.map((m) => (
                    <li
                      key={m.id}
                      className="flex items-center justify-between py-2"
                    >
                      <div>
                        <div className="text-sm font-semibold">
                          {movementTypeLabels[m.type] ?? m.type}
                        </div>
                        {m.reason ? (
                          <div className="text-xs text-fg-muted">
                            {m.reason}
                          </div>
                        ) : null}
                        <div className="text-xs text-fg-subtle">
                          {new Date(m.at).toLocaleTimeString('es-CO')}
                        </div>
                      </div>
                      <div
                        className={
                          m.type === 'out' ||
                          m.type === 'drop' ||
                          m.type === 'refund'
                            ? 'text-danger font-bold'
                            : 'font-bold'
                        }
                      >
                        {formatCOP(m.amountCents)}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-fg-muted">Sin movimientos aún.</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      {tab === 'close' ? (
        <CloseSessionForm
          expected={summary.data?.expectedCashCents ?? 0}
          onSubmit={(amountCents, note) =>
            close.mutate({
              closingCashCents: amountCents,
              ...(note !== undefined ? { note } : {}),
            })
          }
          pending={close.isPending}
          error={close.error?.message ?? null}
        />
      ) : null}
    </div>
  );
};

// ---------- Subcomponents ----------

const OpenSessionForm = ({
  onSubmit,
  pending,
  error,
}: {
  onSubmit: (amountCents: number, note: string | undefined) => void;
  pending: boolean;
  error: string | null;
}) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  return (
    <div className="mx-auto max-w-xl p-8">
      <h1 className="text-3xl font-black tracking-tight">Abrir caja</h1>
      <p className="mt-1 text-fg-muted">
        Ingresa el monto de efectivo con el que abres el turno.
      </p>

      <Card className="mt-6">
        <CardContent className="pt-4">
          <label className="mb-2 block text-sm font-semibold">
            Efectivo inicial (COP)
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="$ 50.000"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-accent"
          />

          <label className="mb-2 mt-4 block text-sm font-semibold">
            Nota (opcional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />

          {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

          <Button
            size="lg"
            className="mt-4 w-full"
            disabled={pending}
            onClick={() => {
              const cents = parseCOPToCents(amount);
              onSubmit(cents, note.trim() === '' ? undefined : note.trim());
            }}
          >
            <Unlock className="size-4" />{' '}
            {pending ? 'Abriendo…' : 'Abrir caja'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const SummaryBlock = ({
  data,
}: {
  data: {
    openingCashCents: number;
    cashSalesCents: number;
    cashInCents: number;
    cashOutCents: number;
    dropsCents: number;
    expectedCashCents: number;
    totalSalesCents: number;
    salesByMethod: {
      cash: number;
      card: number;
      transfer: number;
      nequi: number;
      daviplata: number;
      credit: number;
    };
    creditCents: number;
    collectionsByMethod: Record<CollectionMethod, number>;
    collectionsTotalCents: number;
    cashCollectionsCents: number;
    receivedTotalCents: number;
  };
}) => {
  const methodsWithSales = PAYMENT_METHODS.filter(
    (m) => data.salesByMethod[m] > 0,
  );
  const collectionMethods = COLLECTION_METHODS.filter(
    (m) => (data.collectionsByMethod?.[m] ?? 0) > 0,
  );
  const creditCents = data.creditCents ?? 0;
  const collectionsTotal = data.collectionsTotalCents ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="size-5" /> Ventas del turno
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Stat
              label="Total vendido"
              value={formatCOP(data.totalSalesCents)}
              strong
            />
            {creditCents > 0 || collectionsTotal > 0 ? (
              <Stat
                label="Total recibido"
                value={formatCOP(data.receivedTotalCents)}
                strong
              />
            ) : null}
            {methodsWithSales.length === 0 ? (
              <div className="col-span-full text-sm text-fg-muted">
                Aún no hay pagos registrados en este turno.
              </div>
            ) : (
              methodsWithSales.map((m) => (
                <Stat
                  key={m}
                  label={m === 'credit' ? 'Ventas a crédito' : paymentMethodLabels[m] ?? m}
                  value={formatCOP(data.salesByMethod[m])}
                />
              ))
            )}
          </dl>
          {creditCents > 0 ? (
            <p className="mt-3 text-xs text-fg-muted">
              Las ventas a crédito cuentan como venta pero no son dinero
              recibido: no suman al efectivo esperado.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {collectionsTotal > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="size-5" /> Abonos a cartera
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
              <Stat
                label="Total abonado"
                value={formatCOP(collectionsTotal)}
                strong
              />
              {collectionMethods.map((m) => (
                <Stat
                  key={m}
                  label={paymentMethodLabels[m] ?? m}
                  value={formatCOP(data.collectionsByMethod[m])}
                />
              ))}
            </dl>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="size-5" /> Cajón de efectivo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
            <Stat label="Apertura" value={formatCOP(data.openingCashCents)} />
            <Stat
              label="Ventas efectivo"
              value={formatCOP(data.cashSalesCents)}
            />
            {(data.cashCollectionsCents ?? 0) > 0 ? (
              <Stat
                label="Abonos efectivo"
                value={formatCOP(data.cashCollectionsCents)}
              />
            ) : null}
            <Stat label="Ingresos" value={formatCOP(data.cashInCents)} />
            <Stat label="Egresos" value={formatCOP(data.cashOutCents)} />
            <Stat label="Retiros" value={formatCOP(data.dropsCents)} />
            <Stat
              label="Esperado en caja"
              value={formatCOP(data.expectedCashCents)}
              strong
            />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
};

const Stat = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="rounded-md border border-border bg-surface px-3 py-2">
    <div className="text-xs uppercase tracking-wide text-fg-muted">{label}</div>
    <div
      className={strong ? 'text-xl font-black' : 'text-base font-semibold'}
    >
      {value}
    </div>
  </div>
);

const MovementForm = ({
  title,
  icon: Icon,
  kind,
  onSubmit,
  pending,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  kind: 'in' | 'out';
  onSubmit: (amountCents: number, reason: string | undefined) => void;
  pending: boolean;
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const disabled = pending || parseCOPToCents(amount) <= 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <input
          type="text"
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Monto COP"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Motivo (opcional)"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
        />
        <Button
          variant={kind === 'out' ? 'outline' : 'default'}
          disabled={disabled}
          onClick={() => {
            onSubmit(
              parseCOPToCents(amount),
              reason.trim() === '' ? undefined : reason.trim(),
            );
            setAmount('');
            setReason('');
          }}
        >
          Registrar
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------- Otras cajas abiertas ----------

interface OtherSession {
  id: string;
  userId: string;
  userEmail: string | null;
  userFullName: string | null;
  openedAt: number;
  openingCashCents: number;
  note: string | null;
  totalSalesCents: number;
  expectedCashCents: number;
}

const OtherOpenSessionsCard = ({ sessions }: { sessions: OtherSession[] }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Users className="size-5" /> Otras cajas abiertas
        <span className="ml-auto text-xs font-semibold text-fg-muted">
          {sessions.length}
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ul className="divide-y divide-border">
        {sessions.map((s) => (
          <li key={s.id} className="flex items-start justify-between gap-3 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                {s.userFullName || s.userEmail || 'Usuario'}
              </div>
              {s.userFullName && s.userEmail ? (
                <div className="text-xs text-fg-subtle">{s.userEmail}</div>
              ) : null}
              <div className="text-xs text-fg-muted">
                Abierta {new Date(s.openedAt).toLocaleString('es-CO')}
              </div>
              <div className="mt-1 text-xs text-fg-muted">
                Apertura{' '}
                <strong className="text-fg">
                  {formatCOP(s.openingCashCents)}
                </strong>{' '}
                · Esperado en cajón{' '}
                <strong className="text-fg">
                  {formatCOP(s.expectedCashCents)}
                </strong>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                Ventas
              </div>
              <div className="text-lg font-black tabular-nums">
                {formatCOP(s.totalSalesCents)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

// ---------- Ventas del turno ----------

interface SaleRow {
  id: string;
  origin: OrderOrigin;
  tableLabel: string | null;
  tableNumber: number | null;
  status: OrderStatus;
  totalCents: number;
  closedAt: number | null;
  openedAt: number;
  payments: { method: string; amountCents: number }[];
}

const SalesCard = ({ sales }: { sales: SaleRow[] }) => {
  const [selected, setSelected] = useState<SaleDetailSeed | null>(null);
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="size-5" /> Ventas del turno
            <span className="ml-auto text-xs font-semibold text-fg-muted">
              {sales.length}{' '}
              {sales.length === 1 ? 'pedido con pagos' : 'pedidos con pagos'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sales.length === 0 ? (
            <p className="text-sm text-fg-muted">
              Aún no se han completado pedidos en este turno.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {sales.map((s) => (
                <SaleRowItem
                  key={s.id}
                  sale={s}
                  onOpen={() => setSelected(s)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <SaleDetailModal
        open={selected !== null}
        sale={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

const SaleRowItem = ({
  sale,
  onOpen,
}: {
  sale: SaleRow;
  onOpen: () => void;
}) => {
  const OriginIcon = originIcon(sale.origin);
  const when = sale.closedAt ?? sale.openedAt;
  const tableLabel =
    sale.tableLabel ??
    (sale.tableNumber != null ? String(sale.tableNumber) : null);
  return (
    <li className="py-3">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-1 text-left hover:bg-surface-muted"
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-fg text-fg-inverse">
            <OriginIcon className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-fg-muted">
              <span>
                {originLabels[sale.origin]}
                {tableLabel ? ` · Mesa ${tableLabel}` : ''}
                {' · '}
                <span className="font-mono">
                  #{sale.id.slice(-6).toUpperCase()}
                </span>
              </span>
              <span
                className={
                  'rounded-full border px-2 py-0.5 text-[10px] font-bold ' +
                  orderStatusChipClass(sale.status)
                }
              >
                {orderStatusLabels[sale.status]}
              </span>
            </div>
            <div className="text-xs text-fg-subtle">
              {new Date(when).toLocaleTimeString('es-CO', {
                timeStyle: 'short',
              })}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {sale.payments.length === 0 ? (
                <span className="text-[10px] uppercase tracking-wider text-fg-muted">
                  Sin pagos registrados
                </span>
              ) : (
                sale.payments.map((p, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-muted"
                  >
                    {paymentMethodLabels[p.method] ?? p.method} ·{' '}
                    {formatCOP(p.amountCents)}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="shrink-0 text-lg font-black tabular-nums">
          {formatCOP(sale.totalCents)}
        </div>
      </button>
    </li>
  );
};

// ---------- Abonos a cartera del turno ----------

/**
 * Se monta solo en la tab Ventas: la query de abonos no entra al batch
 * inicial de la página y solo corre si la feature `customer_credit` está on.
 */
const CollectionsCard = () => {
  const features = useFeatures();
  const enabled = features.isEnabled('customer_credit');
  const collections = trpc.cash.collections.useQuery(undefined, {
    enabled,
    refetchInterval: 5_000,
  });
  if (!enabled || !collections.data || collections.data.length === 0) {
    return null;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="size-5" /> Abonos a cartera
          <span className="ml-auto text-xs font-semibold text-fg-muted">
            {collections.data.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {collections.data.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold">
                  {c.customerName ?? 'Cliente'}
                </div>
                <div className="text-xs text-fg-muted">
                  {paymentMethodLabels[c.method] ?? c.method} ·{' '}
                  {new Date(c.receivedAt).toLocaleTimeString('es-CO', {
                    timeStyle: 'short',
                  })}
                  {c.note ? ` · ${c.note}` : ''}
                </div>
              </div>
              <span className="shrink-0 font-bold tabular-nums">
                {formatCOP(c.amountCents)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const CloseSessionForm = ({
  expected,
  onSubmit,
  pending,
  error,
}: {
  expected: number;
  onSubmit: (closingCashCents: number, note: string | undefined) => void;
  pending: boolean;
  error: string | null;
}) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const closing = parseCOPToCents(amount);
  const difference = closing - expected;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="size-4" /> Cerrar turno
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-semibold">
            Efectivo contado en caja (COP)
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="$ 0"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-lg font-bold"
          />
        </div>
        {amount ? (
          <div
            className={
              difference === 0
                ? 'rounded-md border border-border bg-surface px-3 py-2 text-sm'
                : difference > 0
                  ? 'rounded-md border border-border bg-success/10 px-3 py-2 text-sm'
                  : 'rounded-md border border-danger bg-danger/10 px-3 py-2 text-sm text-danger'
            }
          >
            Esperado {formatCOP(expected)} · Contado {formatCOP(closing)} ·
            Diferencia <strong>{formatCOP(difference)}</strong>
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-sm font-semibold">
            Nota (opcional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
        </div>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <Button
          size="lg"
          variant="outline"
          disabled={pending || amount === ''}
          onClick={() =>
            onSubmit(closing, note.trim() === '' ? undefined : note.trim())
          }
        >
          <Lock className="size-4" />{' '}
          {pending ? 'Cerrando…' : 'Cerrar turno'}
        </Button>
      </CardContent>
    </Card>
  );
};
